import { useState, useEffect } from "react"
import { useAuth } from "@/hooks/useAuth"
import { supabase } from "@/integrations/supabase/client"
import { useToast } from "@/hooks/use-toast"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Separator } from "@/components/ui/separator"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select"
import { Building2, Stamp, Bell, Save, Settings2, User, Key, Shield, Smartphone, CheckCircle, AlertTriangle, XCircle, Cookie } from "lucide-react"
import { auditLogger } from "@/utils/auditLogger"
import { DashboardLayout } from "@/components/layout/DashboardLayout"
import { Badge } from "@/components/ui/badge"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Palette } from "lucide-react"
import { CardDescription } from "@/components/ui/card"
import { BrandingTab } from "./Settings_BrandingTab"
import SecuritySettings from "@/components/admin/SecuritySettings"
import { useCookieConsent } from "@/contexts/CookieConsentContext"

interface TenantSettings {
  visit_points: number
  referral_points: number
  stamps_for_reward: number
  enable_notifications: boolean
  notification_email: string
  auto_reward: boolean
  max_stamps_per_day: number
  enable_referral_system: boolean
  visible_stamp_types: string
}

interface TenantData {
  id: string
  name: string
  slug: string
  domain: string | null
  landing_page_url: string | null
  status: string
  settings: any
}

export default function AdminSettings() {
  const { user, loading: authLoading } = useAuth()
  const { toast } = useToast()
  const { setShowSettings: setShowCookieSettings, resetConsent } = useCookieConsent()
  const [tenant, setTenant] = useState<TenantData | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState<{
    name: string
    domain: string
    landing_page_url: string
    settings: TenantSettings
  }>({
    name: "",
    domain: "",
    landing_page_url: "",
    settings: {
      visit_points: 1,
      referral_points: 5,
      stamps_for_reward: 10,
      enable_notifications: true,
      notification_email: "",
      auto_reward: false,
      max_stamps_per_day: 5,
      enable_referral_system: true,
      visible_stamp_types: 'all',
    }
  })
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  })
  const [changingPassword, setChangingPassword] = useState(false)

  // Wallet self-test state
  const [walletTestResults, setWalletTestResults] = useState<any>(null)
  const [walletTesting, setWalletTesting] = useState(false)

  useEffect(() => {
    if (user && !authLoading) {
      fetchTenantData()
    }
  }, [user, authLoading])

  const fetchTenantData = async () => {
    if (!user?.id) {
      setLoading(false)
      return
    }

    try {
      console.log('Fetching tenant data for user:', user.id)
      
      // First check if user has admin role
      const { data: roleData, error: roleError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .single()

      console.log('Role check result:', { roleData, roleError })

      if (roleError || !roleData || roleData.role !== 'admin') {
        throw new Error('Sie haben keine Admin-Berechtigung')
      }

      const { data, error } = await supabase
        .from('tenants')
        .select('*')
        .eq('admin_user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)

      console.log('Tenant query result:', { data, error })

      if (error) {
        console.error('Supabase error:', error)
        throw error
      }

      if (data && data.length > 0) {
        const tenantData = data[0]
        console.log('Setting tenant data:', tenantData)
        setTenant(tenantData as TenantData)
        const settings = (tenantData.settings as any) || {}
        setFormData({
          name: tenantData.name,
          domain: tenantData.domain || "",
          landing_page_url: tenantData.landing_page_url || "",
          settings: {
            visit_points: settings.visit_points || 1,
            referral_points: settings.referral_points || 5,
            stamps_for_reward: settings.stamps_for_reward || 10,
            enable_notifications: settings.enable_notifications ?? true,
            notification_email: settings.notification_email || user?.email || "",
            auto_reward: settings.auto_reward ?? false,
            max_stamps_per_day: settings.max_stamps_per_day || 5,
            enable_referral_system: settings.enable_referral_system ?? true,
            visible_stamp_types: settings.visible_stamp_types || 'all',
          }
        })
      } else {
        console.log('No tenant data found')
        // If no tenant exists, create a default one
        toast({
          title: "Hinweis",
          description: "Kein Unternehmen gefunden. Kontaktieren Sie den Support.",
          variant: "destructive",
        })
      }
    } catch (error) {
      console.error('Error fetching tenant data:', error)
      const errorMessage = error instanceof Error ? error.message : JSON.stringify(error, null, 2)
      console.error('Detailed error:', errorMessage)
      
      toast({
        title: "Fehler",
        description: `Unternehmenseinstellungen konnten nicht geladen werden: ${errorMessage}`,
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!tenant) return

    setSaving(true)
    try {
      const { error } = await supabase
        .from('tenants')
        .update({
          name: formData.name,
          domain: formData.domain || null,
          landing_page_url: formData.landing_page_url || null,
          settings: formData.settings as any
        })
        .eq('id', tenant.id)

      if (error) throw error

      await auditLogger.logAdminAction(
        'update_tenant_settings',
        'tenant',
        tenant.id,
        { 
          name: formData.name,
          domain: formData.domain,
          landing_page_url: formData.landing_page_url,
          settings: formData.settings
        }
      )

      setTenant({
        ...tenant,
        name: formData.name,
        domain: formData.domain || null,
        landing_page_url: formData.landing_page_url || null,
        settings: formData.settings as any
      })

      toast({
        title: "Erfolg",
        description: "Einstellungen wurden erfolgreich gespeichert.",
      })
    } catch (error) {
      console.error('Error updating tenant:', error)
      toast({
        title: "Fehler",
        description: "Einstellungen konnten nicht gespeichert werden.",
        variant: "destructive",
      })
    } finally {
      setSaving(false)
    }
  }

  const updateFormData = (field: string, value: any) => {
    if (field.startsWith('settings.')) {
      const settingField = field.replace('settings.', '')
      setFormData(prev => ({
        ...prev,
        settings: {
          ...prev.settings,
          [settingField]: value
        }
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }))
    }
  }

  const handleChangePassword = async () => {
    // Validate inputs
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({
        title: "Fehler",
        description: "Die Passwörter stimmen nicht überein.",
        variant: "destructive",
      })
      return
    }

    if (passwordData.newPassword.length < 6) {
      toast({
        title: "Fehler", 
        description: "Das Passwort muss mindestens 6 Zeichen lang sein.",
        variant: "destructive",
      })
      return
    }

    setChangingPassword(true)
    try {
      // Changing password for authenticated user does not require current password
      const { error } = await supabase.auth.updateUser({
        password: passwordData.newPassword
      })

      if (error) throw error

      await auditLogger.logAdminAction(
        'password_change',
        'user_account',
        user?.id || 'unknown'
      )

      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      })

      toast({
        title: "Erfolg",
        description: "Passwort wurde erfolgreich geändert.",
      })
    } catch (error) {
      console.error('Error changing password:', error)
      toast({
        title: "Fehler",
        description: "Passwort konnte nicht geändert werden.",
        variant: "destructive",
      })
    } finally {
      setChangingPassword(false)
    }
  }

  const handleWalletSelfTest = async () => {
    setWalletTesting(true)
    setWalletTestResults(null)

    try {
      const { data, error } = await supabase.functions.invoke('wallet-setup-selftest')

      if (error) {
        throw error
      }

      setWalletTestResults(data)

      if (data.summary.overallStatus === 'success') {
        toast({
          title: "✅ Wallet-Setup erfolgreich",
          description: "Alle Tests bestanden. Apple Wallet Pässe sollten funktionieren.",
        })
      } else if (data.summary.overallStatus === 'warning') {
        toast({
          title: "⚠️ Wallet-Setup mit Warnungen",
          description: `${data.summary.warnings} Warnung(en) gefunden. Überprüfen Sie die Details.`,
          variant: "destructive",
        })
      } else {
        toast({
          title: "❌ Wallet-Setup fehlerhaft",
          description: `${data.summary.errors} Fehler gefunden. Zertifikate müssen korrigiert werden.`,
          variant: "destructive",
        })
      }

    } catch (error) {
      console.error('Wallet self-test error:', error)
      toast({
        title: "Fehler",
        description: `Selbsttest fehlgeschlagen: ${error.message}`,
        variant: "destructive",
      })
    } finally {
      setWalletTesting(false)
    }
  }

  if (authLoading || loading) {
    return (
      <DashboardLayout>
        <div className="container mx-auto p-6 space-y-6">
          <h1 className="text-3xl font-bold">Einstellungen</h1>
          <div className="text-center py-8">
            <div className="text-muted-foreground">
              {authLoading ? "Authentifizierung wird überprüft..." : "Lade Einstellungen..."}
            </div>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  if (!user) {
    return (
      <DashboardLayout>
        <div className="container mx-auto p-6 space-y-6">
          <h1 className="text-3xl font-bold">Einstellungen</h1>
          <div className="text-center py-8">
            <div className="text-muted-foreground">Bitte loggen Sie sich ein.</div>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  if (!tenant) {
    return (
      <DashboardLayout>
        <div className="container mx-auto p-6 space-y-6">
          <h1 className="text-3xl font-bold">Einstellungen</h1>
          <div className="text-center py-8">
            <div className="text-muted-foreground">Keine Unternehmensdaten gefunden.</div>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <div className="container mx-auto p-6 space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Einstellungen</h1>
          <Button onClick={handleSave} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Speichert..." : "Speichern"}
          </Button>
        </div>

      <Tabs defaultValue="company" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 gap-1">
          <TabsTrigger value="company" className="flex items-center gap-1 text-xs md:text-sm">
            <Building2 className="h-4 w-4" />
            <span className="hidden sm:inline">Unternehmen</span>
            <span className="sm:hidden">Firma</span>
          </TabsTrigger>
          <TabsTrigger value="stamps" className="flex items-center gap-1 text-xs md:text-sm">
            <Stamp className="h-4 w-4" />
            Stempel
          </TabsTrigger>
          {/* TEMPORARILY HIDDEN: Branding Tab - Code kept for future use
          <TabsTrigger value="branding" className="flex items-center gap-1 text-xs md:text-sm">
            <Settings2 className="h-4 w-4" />
            Branding
          </TabsTrigger> */}
          {/* TEMPORARILY HIDDEN: Wallet Tab - Moved to Superadmin Settings
          <TabsTrigger value="wallet" className="flex items-center gap-1 text-xs md:text-sm">
            <Smartphone className="h-4 w-4" />
            Wallet
          </TabsTrigger> */}
          {/* TEMPORARILY HIDDEN: Security Tab - Code kept for future use
          <TabsTrigger value="security" className="flex items-center gap-1 text-xs md:text-sm">
            <Shield className="h-4 w-4" />
            Sicherheit
          </TabsTrigger> */}
          <TabsTrigger value="account" className="flex items-center gap-1 text-xs md:text-sm">
            <User className="h-4 w-4" />
            Account
          </TabsTrigger>
          <TabsTrigger value="notifications" className="flex items-center gap-1 text-xs md:text-sm">
            <Bell className="h-4 w-4" />
            <span className="hidden sm:inline">Benachrichtigungen</span>
            <span className="sm:hidden">Info</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="company">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Unternehmenseinstellungen
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="company-name">Unternehmensname</Label>
                <Input
                  id="company-name"
                  value={formData.name}
                  onChange={(e) => updateFormData('name', e.target.value)}
                  placeholder="Ihr Unternehmensname"
                />
              </div>

              <div className="space-y-2">
                <Label>Unternehmens-Slug</Label>
                <Input
                  value={tenant.slug}
                  disabled
                  className="bg-muted"
                />
                <p className="text-sm text-muted-foreground">
                  Der Slug kann nicht geändert werden und wird für URLs verwendet
                </p>
              </div>

              <div className="space-y-2">
                <Label>Status</Label>
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${
                    tenant.status === 'active' ? 'bg-green-500' : 'bg-yellow-500'
                  }`} />
                  <span className="capitalize">{tenant.status}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TEMPORARILY HIDDEN: Branding Tab Content - Code kept for future use
        <TabsContent value="branding">
          <BrandingTab />
        </TabsContent> */}

        {/* TEMPORARILY HIDDEN: Security Tab Content - Code kept for future use
        <TabsContent value="security">
          <SecuritySettings />
        </TabsContent> */}

        <TabsContent value="stamps">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Stamp className="h-5 w-5" />
                Stempel-Konfiguration
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Empfehlungssystem aktivieren</Label>
                    <p className="text-sm text-muted-foreground">
                      Kunden können andere Kunden empfehlen und Punkte sammeln
                    </p>
                  </div>
                  <Switch
                    checked={formData.settings.enable_referral_system}
                    onCheckedChange={(checked) => updateFormData('settings.enable_referral_system', checked)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Automatische Prämien</Label>
                    <p className="text-sm text-muted-foreground">
                      Prämien werden automatisch vergeben wenn genug Stempel gesammelt wurden
                    </p>
                  </div>
                  <Switch
                    checked={formData.settings.auto_reward}
                    onCheckedChange={(checked) => updateFormData('settings.auto_reward', checked)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="visible-stamp-types">Sichtbare Stempeltypen für Kunden</Label>
                  <Select
                    value={formData.settings.visible_stamp_types}
                    onValueChange={(value) => updateFormData('settings.visible_stamp_types', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Wählen Sie die sichtbaren Stempeltypen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle Stempel (Besuche & Empfehlungen)</SelectItem>
                      <SelectItem value="visit">Nur Besuchsstempel</SelectItem>
                      <SelectItem value="referral">Nur Empfehlungsstempel</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground">
                    Bestimmt, welche Stempeltypen Ihre Kunden in der App sehen können
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="account">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Account-Verwaltung
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>E-Mail-Adresse</Label>
                <Input
                  value={user?.email || ""}
                  disabled
                  className="bg-muted"
                />
                <p className="text-sm text-muted-foreground">
                  Die E-Mail-Adresse kann derzeit nicht geändert werden
                </p>
              </div>

              <Separator />

              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Key className="h-4 w-4" />
                  Passwort ändern
                </h3>
                
                <div className="space-y-4 max-w-md">
                  <div className="space-y-2">
                    <Label htmlFor="new-password">Neues Passwort</Label>
                    <Input
                      id="new-password"
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                      placeholder="Neues Passwort eingeben"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Passwort bestätigen</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      placeholder="Neues Passwort bestätigen"
                    />
                  </div>

                  <Button 
                    onClick={handleChangePassword}
                    disabled={changingPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                    className="w-full"
                  >
                    <Shield className="h-4 w-4 mr-2" />
                    {changingPassword ? "Passwort wird geändert..." : "Passwort ändern"}
                  </Button>
                </div>

                <div className="bg-muted/50 p-4 rounded-lg">
                  <h4 className="font-medium mb-2">Passwort-Sicherheit:</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Mindestens 6 Zeichen lang</li>
                    <li>• Verwenden Sie eine Kombination aus Buchstaben, Zahlen und Symbolen</li>
                    <li>• Verwenden Sie keine persönlichen Informationen</li>
                    <li>• Teilen Sie Ihr Passwort niemals mit anderen</li>
                  </ul>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Cookie className="h-4 w-4" />
                  Cookie-Einstellungen
                </h3>
                
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Verwalten Sie Ihre Cookie-Präferenzen und Datenschutzeinstellungen für Ihr Unternehmen.
                  </p>
                  
                  <div className="flex gap-3">
                    <Button onClick={() => setShowCookieSettings(true)}>
                      Cookie-Einstellungen öffnen
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        resetConsent()
                        toast({
                          title: "Cookie-Einstellungen zurückgesetzt",
                          description: "Alle Cookie-Präferenzen wurden gelöscht. Sie werden beim nächsten Besuch erneut gefragt."
                        })
                      }}
                    >
                      Einstellungen zurücksetzen
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Benachrichtigungseinstellungen
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Benachrichtigungen aktivieren</Label>
                  <p className="text-sm text-muted-foreground">
                    E-Mail-Benachrichtigungen für wichtige Ereignisse erhalten
                  </p>
                </div>
                <Switch
                  checked={formData.settings.enable_notifications}
                  onCheckedChange={(checked) => updateFormData('settings.enable_notifications', checked)}
                />
              </div>

              {formData.settings.enable_notifications && (
                <div className="space-y-2">
                  <Label htmlFor="notification-email">Benachrichtigungs-E-Mail</Label>
                  <Input
                    id="notification-email"
                    type="email"
                    value={formData.settings.notification_email}
                    onChange={(e) => updateFormData('settings.notification_email', e.target.value)}
                    placeholder="admin@ihr-unternehmen.de"
                  />
                  <p className="text-sm text-muted-foreground">
                    An diese E-Mail-Adresse werden Benachrichtigungen gesendet
                  </p>
                </div>
              )}

              <Separator />

              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Settings2 className="h-4 w-4" />
                  Benachrichtigungstypen
                </h3>
                
                <div className="grid grid-cols-1 gap-4">
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <p className="font-medium">Neue Kundenregistrierung</p>
                      <p className="text-sm text-muted-foreground">
                        Benachrichtigung wenn sich ein neuer Kunde registriert
                      </p>
                    </div>
                    <Switch defaultChecked disabled />
                  </div>

                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <p className="font-medium">Neue Empfehlung</p>
                      <p className="text-sm text-muted-foreground">
                        Benachrichtigung bei erfolgreichen Empfehlungen
                      </p>
                    </div>
                    <Switch defaultChecked disabled />
                  </div>

                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <p className="font-medium">Belohnung erreicht</p>
                      <p className="text-sm text-muted-foreground">
                        Benachrichtigung wenn ein Kunde eine Belohnung erreicht hat
                      </p>
                    </div>
                    <Switch defaultChecked disabled />
                  </div>

                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <p className="font-medium">Täglicher Report</p>
                      <p className="text-sm text-muted-foreground">
                        Tägliche Zusammenfassung der Aktivitäten
                      </p>
                    </div>
                    <Switch disabled />
                  </div>
                </div>

                <p className="text-sm text-muted-foreground">
                  Erweiterte Benachrichtigungsoptionen werden in zukünftigen Updates verfügbar sein.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="wallet">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Smartphone className="h-5 w-5" />
                Apple Wallet Integration
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <p className="text-muted-foreground">
                  Testen Sie die Apple Wallet Integration und Zertifikate.
                </p>
                
                <Button 
                  onClick={handleWalletSelfTest}
                  disabled={walletTesting}
                  className="w-full"
                >
                  {walletTesting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Test läuft...
                    </>
                  ) : (
                    <>
                      <Smartphone className="h-4 w-4 mr-2" />
                      Wallet Integration testen
                    </>
                  )}
                </Button>

                {walletTestResults && (
                  <div className="space-y-4">
                    <div className={`p-4 rounded-lg border ${
                      walletTestResults.summary.overallStatus === 'success' 
                        ? 'bg-green-50 border-green-200'
                        : walletTestResults.summary.overallStatus === 'warning'
                        ? 'bg-yellow-50 border-yellow-200'
                        : 'bg-red-50 border-red-200'
                    }`}>
                      <div className="flex items-center mb-2">
                        {walletTestResults.summary.overallStatus === 'success' && (
                          <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                        )}
                        {walletTestResults.summary.overallStatus === 'warning' && (
                          <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2" />
                        )}
                        {walletTestResults.summary.overallStatus === 'error' && (
                          <XCircle className="h-5 w-5 text-red-600 mr-2" />
                        )}
                        <h3 className="font-semibold">
                          {walletTestResults.summary.overallStatus === 'success' && 'Test erfolgreich'}
                          {walletTestResults.summary.overallStatus === 'warning' && 'Test mit Warnungen'}
                          {walletTestResults.summary.overallStatus === 'error' && 'Test fehlgeschlagen'}
                        </h3>
                      </div>
                      
                      <div className="text-sm space-y-1">
                        <div>✅ Erfolg: {walletTestResults.summary.success}</div>
                        {walletTestResults.summary.warnings > 0 && (
                          <div>⚠️ Warnungen: {walletTestResults.summary.warnings}</div>
                        )}
                        {walletTestResults.summary.errors > 0 && (
                          <div>❌ Fehler: {walletTestResults.summary.errors}</div>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <h4 className="font-medium">Detaillierte Ergebnisse:</h4>
                      <div className="text-sm space-y-1 max-h-40 overflow-y-auto">
                        {walletTestResults.tests.map((test: any, index: number) => (
                          <div key={index} className={`flex items-center gap-2 p-2 rounded ${
                            test.status === 'success' ? 'text-green-700' :
                            test.status === 'warning' ? 'text-yellow-700' : 'text-red-700'
                          }`}>
                            {test.status === 'success' && '✅'}
                            {test.status === 'warning' && '⚠️'}
                            {test.status === 'error' && '❌'}
                            <span>{test.name}: {test.message}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Benachrichtigungseinstellungen
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <p className="font-medium">Neue Empfehlung</p>
                      <p className="text-sm text-muted-foreground">
                        Benachrichtigung bei neuen Empfehlungen
                      </p>
                    </div>
                    <Switch defaultChecked disabled />
                  </div>

                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <p className="font-medium">Erfolgreiche Empfehlung</p>
                      <p className="text-sm text-muted-foreground">
                        Benachrichtigung bei erfolgreichen Empfehlungen
                      </p>
                    </div>
                    <Switch defaultChecked disabled />
                  </div>

                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <p className="font-medium">Belohnung erreicht</p>
                      <p className="text-sm text-muted-foreground">
                        Benachrichtigung wenn ein Kunde eine Belohnung erreicht hat
                      </p>
                    </div>
                    <Switch defaultChecked disabled />
                  </div>

                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <p className="font-medium">Täglicher Report</p>
                      <p className="text-sm text-muted-foreground">
                        Tägliche Zusammenfassung der Aktivitäten
                      </p>
                    </div>
                    <Switch disabled />
                  </div>
                </div>

                <p className="text-sm text-muted-foreground">
                  Erweiterte Benachrichtigungsoptionen werden in zukünftigen Updates verfügbar sein.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      </div>
    </DashboardLayout>
  )
}